import { pickT } from "@repo/shared";
import type { FlowField, GoalTypeOption } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { isComponentAllowedOnPageType } from "@/components/flow/flow-component-types";
import type { GroupAnsweringNotes } from "@/components/flow/group-answering-notes";
import { useInvalidateGoalSnapshots } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { goalQueryKeys } from "@/lib/hooks/use-flow-instances/use-goals";
import {
  useRiskAssessmentValue,
  useSubmitRiskAssessment,
} from "@/lib/hooks/use-flow-instances/use-risk-assessment";
import { FlowLanguageScope } from "@/lib/i18n/flow-language-scope";
import { log } from "@/lib/logger/browser-logger";
import type {
  FlowPage,
  FlowPageComponent,
  FlowPageWithComponents,
  GoalInputConfig,
  RiskAssessmentConfig,
} from "@/lib/types/flow";
import { GoalInputWidget } from "@/pages/flow-instances/goals/goal-input-widget";
import { AdvisorNotesEditor } from "../advisor-notes-editor";
import {
  renderEmbed,
  renderFile,
  renderIframe,
  renderImage,
  renderModal,
  renderSpacer,
  renderTextBlock,
  renderYoutubeVideo,
} from "../flow-component-renderers";
import { MisplacedComponentNotice } from "../misplaced-component-notice";
import type { ActingRepresentative } from "../representative-note";
import { RiskAssessmentWidget } from "../risk-assessment-widget";
import {
  AccountCashOverview,
  AssetClassForecast,
  CashTransfers,
  PortfolioList,
} from "../self-contained";
import { EquityShareChartWidget } from "../self-contained/equity-share-chart-widget";
import { MandateComparisonWidget } from "../self-contained/mandate-comparison-widget";
import { MandateWidget } from "../self-contained/mandate-widget";
import { VolatilityChartWidget } from "../self-contained/volatility-chart-widget";
import type { ConditionEvaluationData } from "./flow-wizard-utils";
import { WizardFieldRenderer } from "./wizard-field-renderer";
import { T } from "./wizard-goal-page.i18n";
import { WizardPageRenderer } from "./wizard-page-renderer";

export type GoalData = {
  id: string;
  name: string;
  goalType: string;
  /** QP-187 — optional free-text description (max 150 chars). */
  description?: string | null;
  scheduleType: string;
  oneTimeAmount: number | null;
  oneTimeDate: Date | string | null;
  recurringAmount: number | null;
  recurringFrequency: string | null;
  recurringStartDate: Date | string | null;
  recurringEndDate: Date | string | null;
  portfolioType: string;
  hasRiskAssessment: boolean;
  riskScore: number | null;
  // QP-180 — score decomposition (riskScore stays the FINAL effective score)
  riskScoreRaw?: number | null;
  riskScoreAdjustment?: number;
  riskScoreCap?: number | null;
  isRiskScoreCapped?: boolean;
  timeHorizonId: string | null;
  riskLevelId: string | null;
  selectedPortfolioId: string | null;
  customProducts?: any[];
  timeHorizon?: { label: string };
  riskLevel?: { label: string };
  updatedAt?: string;
  createdAt?: string;
};

type WizardGoalPageProps = {
  page: FlowPage;
  goalId: string;
  instanceId: string;
  goals: GoalData[];
  clientName: string;
  conditionData: ConditionEvaluationData;
  isCompleted: boolean;
  flowLanguage: string;
  flowName: string;
  riskModelId: string | null;
  /** QP-187 — the flow collection's goal types (feeds the goal_input type picker). */
  goalTypes?: GoalTypeOption[];
  chartColors?: string[] | null;
  showValidationErrors?: boolean;
  /** `"goalId:componentId"` — set when a goal-scoped widget on this goal has an error. */
  missingGoalComponentKeys?: Set<string>;
  missingFieldKeys?: Set<string>;
  missingTabKeys?: Set<string>;
  /** QL-43 — `"goalId:pageId"` keys whose required advisor note is still empty. */
  missingAdvisorNoteKeys?: Set<string>;
  /** QP-184 — Art. 54(6): K&E-related adjustment answers are the representative's. */
  actingRepresentative?: ActingRepresentative | null;
  /** QL-188 — group answering notes; null for non-group clients. */
  groupNotes?: GroupAnsweringNotes | null;
};

export function WizardGoalPage({
  page,
  goalId,
  instanceId,
  goals,
  clientName,
  conditionData,
  isCompleted,
  flowLanguage,
  flowName,
  riskModelId,
  goalTypes,
  chartColors,
  showValidationErrors,
  missingGoalComponentKeys,
  missingFieldKeys,
  missingTabKeys,
  missingAdvisorNoteKeys,
  actingRepresentative,
  groupNotes = null,
}: WizardGoalPageProps) {
  // Above its own FlowLanguageScope, so the flow language is picked explicitly.
  const t = pickT(T, flowLanguage);
  const queryClient = useQueryClient();
  const submitRiskAssessment = useSubmitRiskAssessment(instanceId);
  const invalidateSnapshots = useInvalidateGoalSnapshots();

  // After goal input save, invalidate goals query + all snapshots for this goal.
  // The volatility chart is covered by COMPANION_KEYS in useInvalidateGoalSnapshots
  // when assetClassForecast appears in refreshHints.
  const handleGoalSaveComplete = useCallback(
    (refreshHints?: { invalidated?: string[]; cleared?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: goalQueryKeys.goals(instanceId) });
      invalidateSnapshots(goalId, refreshHints as any);
    },
    [queryClient, instanceId, goalId, invalidateSnapshots],
  );

  // Get current goal info
  const currentGoal = goals.find((g) => g.id === goalId);

  // QP-180 — the persisted risk-assessment inputData carries the client's
  // adjustment-question answers (the goal columns don't); seed the widget with them.
  // QL-212 — this read returns the goal's EFFECTIVE inputs: an input the model
  // captures once for the client arrives here too, even though the goal's own
  // row holds no copy of it.
  const { inputData: riskInputData } = useRiskAssessmentValue(instanceId, goalId);
  const persistedRiskInput = (() => {
    if (!riskInputData) return undefined;
    try {
      const parsed = JSON.parse(riskInputData);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as { timeHorizonId?: string; riskLevelId?: string; adjustmentAnswers?: unknown })
        : undefined;
    } catch {
      return undefined;
    }
  })();
  const rawRiskAnswers = persistedRiskInput?.adjustmentAnswers;
  const persistedAdjustmentAnswers =
    rawRiskAnswers && typeof rawRiskAnswers === "object" && !Array.isArray(rawRiskAnswers)
      ? (rawRiskAnswers as Record<string, string>)
      : undefined;

  // Helper to get risk assessment snapshot from goal data (used by RiskAssessmentWidget + goalRenderContext)
  const getGoalSnapshot = (snapshotType: string, snapshotKey: string) => {
    if (!currentGoal) return undefined;

    if (snapshotType === "risk_assessment" && snapshotKey === "risk_assessment") {
      if (currentGoal.hasRiskAssessment && currentGoal.riskScore != null) {
        return JSON.stringify({
          timeHorizonId: currentGoal.timeHorizonId,
          riskLevelId: currentGoal.riskLevelId,
          score: currentGoal.riskScore,
          // QP-180 — the widget seeds its matrix state from the RAW score;
          // riskScore is the final effective value (raw + adjustment, capped).
          scoreRaw: currentGoal.riskScoreRaw ?? currentGoal.riskScore,
          // QP-180 — the client's adjustment-question answers (from inputData).
          ...(persistedAdjustmentAnswers && { adjustmentAnswers: persistedAdjustmentAnswers }),
          timeHorizonName: currentGoal.timeHorizon?.label || "",
          riskLevelName: currentGoal.riskLevel?.label || "",
          timestamp: currentGoal.updatedAt || currentGoal.createdAt,
        });
      }

      // QL-212 — no score yet, but something HAS been answered: this goal's own
      // half, or the client-level half captured on a global page. Seed the widget
      // with the merged inputs so the sections this page does not render still
      // feed the score readout, and a half-finished answer survives a reload.
      if (
        persistedRiskInput?.timeHorizonId ||
        persistedRiskInput?.riskLevelId ||
        persistedAdjustmentAnswers
      ) {
        return JSON.stringify({
          timeHorizonId: persistedRiskInput?.timeHorizonId ?? "",
          riskLevelId: persistedRiskInput?.riskLevelId ?? "",
          ...(persistedAdjustmentAnswers && { adjustmentAnswers: persistedAdjustmentAnswers }),
        });
      }
    }

    return undefined;
  };

  // Render goal-scoped component
  const renderGoalComponent = (component: FlowPageComponent & { flowField?: FlowField }) => {
    const isDisabled = isCompleted;
    // Goal-scoped widgets (risk_assessment, custom_portfolio, portfolio_display) emit a
    // missing entry per goal but share a single component definition across goals on the
    // same goal page — we MUST key the lookup on `goalId:componentId` so a gap on one goal
    // doesn't paint the same widget on a sibling goal.
    const componentHasError = !!(
      showValidationErrors && missingGoalComponentKeys?.has(`${goalId}:${component.id}`)
    );

    // Guard: a component placed on a page type it is not allowed on (e.g. a
    // global-only component on this goal page) renders a generic misplaced notice
    // instead of wrong-scope data. Single source of truth: isComponentAllowedOnPageType.
    if (!isComponentAllowedOnPageType(component.componentType, "goal")) {
      return (
        <MisplacedComponentNotice
          componentType={component.componentType}
          pageType="goal"
          language={flowLanguage}
        />
      );
    }

    switch (component.componentType) {
      case "field":
        if (!component.flowField) {
          return <div className="text-sm text-destructive">{t.fieldNotFound}</div>;
        }
        {
          const fieldKey =
            component.flowField.scope === "goal"
              ? `${goalId}:${component.flowField.id}`
              : component.flowField.id;
          const fieldHasError = !!(showValidationErrors && missingFieldKeys?.has(fieldKey));
          return (
            <WizardFieldRenderer
              field={component.flowField}
              instanceId={instanceId}
              isCompleted={isCompleted}
              goalId={goalId}
              hasValidationError={fieldHasError}
            />
          );
        }

      case "risk_assessment": {
        if (!riskModelId) {
          return <div className="text-sm text-destructive">{t.riskModelNotConfigured}</div>;
        }
        let riskConfig: RiskAssessmentConfig = {};
        try {
          riskConfig = component.config ? JSON.parse(component.config) : {};
        } catch {
          // Invalid config, use defaults
        }
        const riskSnapshotValue = getGoalSnapshot("risk_assessment", "risk_assessment");

        return (
          <RiskAssessmentWidget
            riskModelId={riskModelId}
            pageType="goal"
            goalId={goalId}
            value={riskSnapshotValue}
            onChange={(value, isComplete) => {
              try {
                const riskData = JSON.parse(value);
                // QL-212 — "complete" means every input section THIS page renders
                // has an answer, which only the widget can know: under a mixed
                // model a goal page may own the time horizon alone. The SCORE is
                // never a client-side decision — the server derives it from the
                // risk matrix — so it does not gate commit-vs-autosave either.
                submitRiskAssessment.mutate({ goalId, data: riskData, isComplete });
              } catch (error) {
                log.error({ err: error }, "Failed to parse risk assessment data:");
              }
            }}
            disabled={isDisabled}
            style={riskConfig.style}
            title={riskConfig.title}
            description={riskConfig.description}
            hasValidationError={componentHasError}
            finalScore={currentGoal?.riskScore ?? null}
            isScoreCapped={currentGoal?.isRiskScoreCapped ?? false}
            actingRepresentative={actingRepresentative}
            groupNote={groupNotes?.riskAssessment}
          />
        );
      }

      // QP-150 — portfolio_display / portfolio_xray / asset_allocation / historical_return /
      // cost_table / cost_chart / expected_value / portfolio_forecast / product_costs are
      // portfolio-scoped now: they render on the portfolio page (PortfolioPageRenderer), not here.
      // The placement guard above returns a MisplacedComponentNotice if one is mis-placed on a
      // goal page.

      case "text_block":
        return renderTextBlock(component, flowLanguage);

      case "spacer":
        return renderSpacer(component);

      case "youtube_video":
        return renderYoutubeVideo(component);
      case "iframe":
        return renderIframe(component);

      case "image":
        return renderImage(component);

      case "embed":
        return renderEmbed(component);

      case "modal":
        return renderModal(component, flowLanguage);

      case "file":
        return renderFile(component, flowLanguage);

      case "goal_input": {
        // QP-150 — goal_input is goal-level only now (name). Schedule/amount/portfolio-type
        // moved to portfolio_input on the portfolio page.
        let goalInputConfig: GoalInputConfig = {};
        try {
          goalInputConfig = component.config ? JSON.parse(component.config) : {};
        } catch {
          // Invalid config, use defaults
        }

        const goalData = currentGoal
          ? {
              name: currentGoal.name,
              goalType: currentGoal.goalType,
              description: currentGoal.description ?? null,
            }
          : undefined;

        return (
          <GoalInputWidget
            flowInstanceId={instanceId}
            goalId={goalId}
            initialData={goalData}
            disabled={isDisabled}
            style={goalInputConfig.style}
            title={goalInputConfig.title}
            description={goalInputConfig.description}
            goalTypes={goalTypes}
            showDescription={goalInputConfig.showDescription === true}
            onSaveComplete={handleGoalSaveComplete}
            hasValidationError={componentHasError}
            groupNote={groupNotes?.goals}
          />
        );
      }

      // QP-150 — custom_portfolio is portfolio-scoped now (rendered on the portfolio page
      // via PortfolioPageRenderer). The placement guard above handles a mis-placed instance.

      case "portfolio_list": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <PortfolioList
            instanceId={instanceId}
            goalId={goalId}
            goals={goals.map((g) => ({ id: g.id, name: g.name }))}
            config={cfg}
            language={flowLanguage}
          />
        );
      }

      // QL-315 — the only aggregate analytics component allowed on a goal page.
      // It merges this goal's portfolios, which is unambiguous because goals
      // partition portfolios; nothing here is double-counted.
      case "asset_class_forecast": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <AssetClassForecast
            goalId={goalId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
          />
        );
      }

      // QL-314 — volatility line chart with compliance gate.
      case "volatility_chart": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <VolatilityChartWidget
            instanceId={instanceId}
            goalId={goalId}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
            disabled={isDisabled}
          />
        );
      }

      // QL-313 — equity share stacked bar with compliance gate.
      case "equity_share_chart": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <EquityShareChartWidget
            instanceId={instanceId}
            goalId={goalId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            hasValidationError={componentHasError}
            disabled={isDisabled}
          />
        );
      }

      // QL-375 — the mandate check: the goal's combined allocation against its mandate.
      case "mandate": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <MandateWidget
            instanceId={instanceId}
            goalId={goalId}
            scope="goal"
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
            disabled={isDisabled}
          />
        );
      }
      // QL-375 group 8 — existing vs new mandate on a follow-up goal (screen 12).
      case "mandate_comparison": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return <MandateComparisonWidget instanceId={instanceId} goalId={goalId} config={cfg} />;
      }

      // QL-287 — instance-wide data: a goal page shows the same accounts and
      // transfers as a global page.
      case "account_cash_overview": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return <AccountCashOverview instanceId={instanceId} config={cfg} />;
      }

      case "cash_transfers": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return <CashTransfers instanceId={instanceId} config={cfg} disabled={isCompleted} />;
      }

      default:
        return null;
    }
  };

  // Get page with components
  const pageWithComponents = page as FlowPageWithComponents;
  const layouts = pageWithComponents.layouts || [];

  return (
    <div className="space-y-6">
      <FlowLanguageScope language={flowLanguage}>
        <WizardPageRenderer
          layouts={layouts}
          components={pageWithComponents.components}
          conditionData={{ ...conditionData, goalId }}
          renderComponent={renderGoalComponent}
          showValidationErrors={showValidationErrors}
          missingTabKeys={missingTabKeys}
        />
      </FlowLanguageScope>

      {/* Advisor Notes */}
      {page.enableAdvisorNotes && (
        <AdvisorNotesEditor
          flowInstanceId={instanceId}
          flowPageId={page.id}
          goalId={goalId}
          isEditable={!isCompleted}
          isRequired={page.requireAdvisorNotes === true}
          hasValidationError={
            !!showValidationErrors && !!missingAdvisorNoteKeys?.has(`${goalId}:${page.id}`)
          }
          context={{
            riskScore: currentGoal?.riskScore ?? undefined,
            clientName,
            flowName,
            pageName: page.name,
            language: flowLanguage,
          }}
        />
      )}
    </div>
  );
}
